import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import BackToTopButton from './BackToTopButton';

interface FaqItem {
  question: string;
  answer: React.ReactNode;
}

export default function Faq() {
  return (
    <>
      <style>{faqStyles}</style>

      <main className="faq-page">
        <header className="faq-hero">
          <nav className="faq-breadcrumb" aria-label="breadcrumb">
            <a href="/">
              <svg aria-hidden="true" viewBox="0 0 24 24">
                <path d="M15 18l-6-6 6-6" />
              </svg>
              Home
            </a>
          </nav>

          <p className="faq-eyebrow">Investing</p>
          <h1>FAQ</h1>
        </header>

        <section className="faq-list" aria-label="FAQ">
          {faqItems.map((item, index) => (
            <details className="faq-item" key={item.question} open={index === 0}>
              <summary>
                <span className="faq-question">{item.question}</span>
                <span className="faq-chevron" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </span>
              </summary>
              <div className="faq-answer">{item.answer}</div>
            </details>
          ))}
        </section>
      </main>

      <BackToTopButton />
    </>
  );
}

const rankingData = `
Decode Investing employs a robust algorithm to analyze public companies around the world, ranking them based on their long-term historical fundamentals. This analysis aims to provide an estimated valuation for each stock using the company’s financial history.

#### Daily Leaderboard

The global leaderboard showcases highly ranked, actively traded public companies according to their fundamentals. You can search the current results and filter them by industry, region, country, or market index. The ranking is updated as new financial records are indexed and analyzed. You can view the leaderboard [here](https://www.decodeinvesting.com/leaderboard).

#### Analytical Process

The algorithm evaluates companies through various steps to generate a comprehensive value score, which serves as an overall rating of the business’s fundamentals. The value score is composed of the ROIC score, debt score, and moat score.

**1. ROIC Score**
   - Assesses the company’s Return on Invested Capital (ROIC) over the past ten years.
   - Each year is individually scored. A year with ROIC of 10% or above receives a perfect score.
   - The overall ROIC score is the mean of all yearly scores.

**2. Debt Score**
   - Measures the effectiveness of the company’s debt management.
   - Calculated from the debt payoff value, which is the time required to pay off current long-term debt using free cash flow.
   - A perfect debt score is achieved if long-term debt is zero.

**3. Management Score**
   - Represents the management’s performance, combining 80% of the ROIC score and 20% of the debt score.

**4. Moat Score**
   - Evaluates the company's competitive advantage through historical CAGR growth rates, including revenue, operating cash flow, and net income.
   - A perfect moat score requires consistent growth rates of 10% or above over one, three, five, seven, and ten years.

**5. Value Score**
   - Represents the overall fundamentals of a company, combining 50% moat score and 50% management score.

#### Leaderboard Details

The ranking system follows Rule One investing principles. Companies on the global leaderboard must have a value score of at least 90 and at least seven years of financial history. ETFs and funds are excluded, the company must be actively trading, and ADRs remain eligible.

#### Margin-Of-Safety Price

A fair value and margin-of-safety price are calculated using a simplified DCF method. This includes:

- An estimate of the 10-year growth rate.
- Future PE of the business.
- Current EPS (ttm).

The default growth rate is capped at 20% to prevent overly optimistic assumptions.

#### Payback Time

Payback time measures how long it will take to recoup investment capital from free cash flow:

- Calculated by projecting free cash flow growth until it equals the market cap.
- A payback time of 10 years or less is ideal, with 5 years being an extreme bargain.

For more detailed insights, check out [How Decode Investing's Ranking Algorithm Works: A Deep Dive 🚀](https://new.reddit.com/r/DecodeInvesting/comments/1erhv2k/how_decode_investings_ranking_algorithm_works_a/) and [How to calculate the true value of a business](https://decode-investing.medium.com/decode-investing-part-3-how-to-calculate-the-true-value-of-a-business-700dbbd0b728).

This algorithm provides a powerful tool for analyzing and ranking public companies, helping investors make more informed decisions. Feel free to explore the leaderboard and leverage these insights for your investment strategies!
`;

const faqItems: FaqItem[] = [
  {
    question: 'What is Decode Investing?',
    answer: (
      <p>
        Decode investing is a set of tools that help investors discover opportunities and identify risks in the stock market.
      </p>
    ),
  },
  {
    question: 'Why did you build Decode Investing?',
    answer: (
      <>
        <p>
          Value investing is a form of investing used by some of the most successful investors in the world, but there is a lack of mainstream adoption of value investing.
          It is simple in concept but difficult to put into practice. I wanted to help make value investing more accessible to the average investor.
        </p>
        <p>
          <a href="https://www.youtube.com/channel/UCA8P3Rgfjn0IFZL4bmrKbrg">Phil Town</a> started a movement with <a href="https://www.amazon.com/Rule-Strategy-Successful-Investing-Minutes/dp/0307336840/">Rule One Investing</a>, he made value investing approachable and practical, he put it in a way that anyone can learn and apply it successfully.
          A problem I noticed is that there is a lack of modern software tools for Value Investing.
          Most existing tools have not kept up with advancements in technology and software development.
        </p>
        <p>
          I want to help pass on this amazing principle and strategy called Value Investing to a new generation of Investors.
          I want to use the best technologies available today to build the best modern software tools for the next generation of value investors.
        </p>
      </>
    ),
  },
  {
    question: 'Are you an Investment Education company?',
    answer: (
      <p>
        No, we are a software company, our first goal is to build great software tools that make it easy for anyone to apply sound investing principles and make great investment returns.
        Our product is still in a very early beta version, but we are iterating very fast and making improvements.
      </p>
    ),
  },
  {
    question: 'What resources do you recommend to learn more about modern value investing?',
    answer: (
      <p>
        I wrote this small introduction to value investing <a href="https://decode-investing.medium.com/how-to-make-higher-returns-in-the-stock-market-in-a-safe-and-predictable-way-cd51bb8bc5c9">How to make impressive returns from the stock market</a>.
        I also recommend these 3 books for an introduction and practical guide to Value Investing, <a href="https://www.amazon.com/Rule-Strategy-Successful-Investing-Minutes/dp/0307336840/">Rule #1 by Phil Town</a>, <a href="https://www.amazon.com/Payback-Time-Outsmarting-Getting-Investments/dp/1847940641/">Payback Time by Phil Town</a>,
        and <a href="https://www.amazon.com/Education-Value-Investor-Transformative-Enlightenment/dp/1137278811/">The education of a Value Investor by Guy Spier</a>. <a href="https://www.youtube.com/channel/UCA8P3Rgfjn0IFZL4bmrKbrg">Phil Town’s Youtube channel</a> is also very good, also check out Warren Buffet interviews on Youtube especially the ones from Berkshire Hathaway's annual shareholder meeting.
      </p>
    ),
  },
  {
    question: 'If Value Investing is so good why isn’t everyone doing it?',
    answer: (
      <p>
        Value Investing is difficult to put into practice without the proper tools. We are building those tools, we want to change the way the average investor analyzes companies and investments.
        Technology can help us make much better investing decisions.
      </p>
    ),
  },
  {
    question: 'What algorithms are you using to rank companies on your website?',
    answer: <ReactMarkdown remarkPlugins={[remarkGfm]}>{rankingData}</ReactMarkdown>,
  },
  {
    question: 'Are you a financial Advisor?',
    answer: <p>No, we are a software company.</p>,
  },
  {
    question: 'How accurate is your data?',
    answer: (
      <p>
        We index our own data directly from the SEC Edgar system. We pull and index company SEC filings and then extract their financial data from XBRL documents every day. We try our best to ensure that all our data is accurate. However, the current version of our product is still a very early MVP (Minimum viable product). There may be some inaccuracies or limitations. We are working hard to ensure that all our data is accurate, and we are iterating very fast, fixing any issues or bugs that arise. We also want to thank you for using this very early version of our product. We encourage you to do your own research (using different sources) before making any investment decision. Our current product is a good starting point in your analysis to determine if a company will make a good investment in your portfolio. I can guarantee that our algorithms' data and analysis on the site are mostly accurate. I am a value investor, and I am already using it. We felt it was ready for public release while iterated and made improvements.
        We also use data provided by <a href="https://financialmodelingprep.com/developer/docs/" target="_blank">Financial Modeling Prep</a> for Stock Quotes and other financial data.
      </p>
    ),
  },
  {
    question: 'Who is behind this?',
    answer: (
      <p>
        My name is Obi Akubue, I am a software engineer and value investor. I am also the founder of Decode Investing. You can reach me at <a href="mailto:obi@decodeinvesting.com">obi@decodeinvesting.com</a>.
      </p>
    ),
  },
  {
    question: 'How can I support the community?',
    answer: (
      <p>
        Check out our <a target="_blank" href="/blog">blog</a>. Join us on reddit <a target="_blank" href="https://www.reddit.com/r/DecodeInvesting">/r/DecodeInvesting/</a> and <a target="_blank" href="https://www.reddit.com/r/EarningsCalls/">/r/EarningsCalls/</a>
      </p>
    ),
  },
];

const faqStyles = `
  .faq-page {
    --faq-accent: #7c3aed;
    --faq-accent-strong: #6426cf;
    --faq-accent-soft: rgba(124, 58, 237, 0.09);
    --faq-border: #e7e1ee;
    --faq-heading: #241b2f;
    --faq-muted: #6f6579;
    --faq-surface: #ffffff;
    --faq-surface-soft: #faf8fc;
    --faq-success: #198754;
    max-width: 940px;
    margin: 0 auto;
    padding: 0 0 4rem;
    color: var(--faq-heading);
  }

  .faq-hero {
    position: relative;
    isolation: isolate;
    overflow: hidden;
    margin-bottom: 1.5rem;
    padding: clamp(1.5rem, 4vw, 2.75rem);
    border: 1px solid var(--faq-border);
    border-radius: 24px;
    background:
      radial-gradient(circle at 88% 8%, rgba(124, 58, 237, 0.17), transparent 30%),
      linear-gradient(135deg, var(--faq-surface) 0%, var(--faq-surface-soft) 100%);
    box-shadow: 0 18px 48px rgba(43, 28, 58, 0.08);
  }

  .faq-hero::after {
    position: absolute;
    z-index: -1;
    right: -54px;
    bottom: -84px;
    width: 190px;
    height: 190px;
    border: 28px solid rgba(25, 135, 84, 0.08);
    border-radius: 50%;
    content: "";
  }

  .faq-breadcrumb {
    margin-bottom: clamp(1.5rem, 4vw, 2.5rem);
  }

  .faq-breadcrumb a {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    color: var(--faq-muted);
    font-size: 0.875rem;
    font-weight: 600;
    text-decoration: none;
    transition: color 160ms ease;
  }

  .faq-breadcrumb a:hover {
    color: var(--faq-accent);
    text-decoration: none;
  }

  .faq-breadcrumb a:focus-visible,
  .faq-answer a:focus-visible {
    border-radius: 4px;
    outline: 3px solid rgba(124, 58, 237, 0.26);
    outline-offset: 3px;
  }

  .faq-breadcrumb svg {
    width: 1rem;
    height: 1rem;
    fill: none;
    stroke: currentColor;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 2;
  }

  .faq-eyebrow {
    display: inline-flex;
    margin: 0 0 0.8rem;
    padding: 0.35rem 0.7rem;
    border-radius: 999px;
    background: rgba(25, 135, 84, 0.11);
    color: var(--faq-success);
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.09em;
    line-height: 1;
    text-transform: uppercase;
  }

  .faq-hero h1 {
    max-width: 700px;
    margin: 0;
    color: var(--faq-heading);
    font-size: clamp(2.35rem, 7vw, 4.5rem);
    font-weight: 800;
    letter-spacing: -0.045em;
    line-height: 0.98;
  }

  .faq-list {
    display: grid;
    gap: 0.75rem;
  }

  .faq-item {
    overflow: hidden;
    border: 1px solid var(--faq-border);
    border-radius: 16px;
    background: var(--faq-surface);
    box-shadow: 0 5px 18px rgba(43, 28, 58, 0.045);
    scroll-margin-top: 6rem;
    transition: border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease;
  }

  .faq-item:hover {
    border-color: rgba(124, 58, 237, 0.34);
    transform: translateY(-1px);
  }

  .faq-item[open] {
    border-color: rgba(124, 58, 237, 0.42);
    box-shadow: 0 12px 30px rgba(64, 40, 88, 0.09);
  }

  .faq-item summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.25rem 1.4rem;
    cursor: pointer;
    list-style: none;
    user-select: none;
  }

  .faq-item summary::-webkit-details-marker {
    display: none;
  }

  .faq-item summary:focus {
    outline: none;
  }

  .faq-item summary:focus-visible {
    border-radius: 15px;
    outline: 3px solid rgba(124, 58, 237, 0.24);
    outline-offset: -3px;
  }

  .faq-question {
    color: var(--faq-heading);
    font-size: clamp(1rem, 2vw, 1.075rem);
    font-weight: 700;
    letter-spacing: -0.01em;
    line-height: 1.45;
  }

  .faq-chevron {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background: var(--faq-accent-soft);
    color: var(--faq-accent);
  }

  .faq-chevron svg {
    width: 1rem;
    height: 1rem;
    fill: none;
    stroke: currentColor;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 2.25;
    transition: transform 180ms ease;
  }

  .faq-item[open] .faq-chevron {
    background: var(--faq-accent);
    color: #ffffff;
  }

  .faq-item[open] .faq-chevron svg {
    transform: rotate(180deg);
  }

  .faq-answer {
    max-width: 78ch;
    padding: 0 4.65rem 1.5rem 1.4rem;
    color: var(--faq-muted);
    font-size: 0.975rem;
    line-height: 1.78;
    overflow-wrap: anywhere;
    animation: faq-answer-in 180ms ease-out;
  }

  .faq-answer p {
    margin: 0 0 1rem;
  }

  .faq-answer p:last-child,
  .faq-answer ul:last-child {
    margin-bottom: 0;
  }

  .faq-answer h4 {
    margin: 1.75rem 0 0.65rem;
    color: var(--faq-heading);
    font-size: 1rem;
    font-weight: 750;
    letter-spacing: -0.005em;
    line-height: 1.45;
  }

  .faq-answer h4:first-child {
    margin-top: 0;
  }

  .faq-answer ul {
    margin: 0.5rem 0 1.1rem;
    padding-left: 1.35rem;
  }

  .faq-answer li {
    margin-bottom: 0.4rem;
    padding-left: 0.25rem;
  }

  .faq-answer strong {
    color: var(--faq-heading);
    font-weight: 700;
  }

  .faq-answer a {
    color: var(--faq-accent-strong);
    font-weight: 600;
    text-decoration: underline;
    text-decoration-color: rgba(124, 58, 237, 0.34);
    text-decoration-thickness: 1px;
    text-underline-offset: 0.18em;
    transition: color 160ms ease, text-decoration-color 160ms ease;
  }

  .faq-answer a:hover {
    color: var(--faq-accent);
    text-decoration-color: currentColor;
  }

  @keyframes faq-answer-in {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  html.dark-mode .faq-page {
    --faq-accent: #bd8cff;
    --faq-accent-strong: #d1aeff;
    --faq-accent-soft: rgba(189, 140, 255, 0.13);
    --faq-border: #3a3442;
    --faq-heading: #f7f3fb;
    --faq-muted: #c1b8ca;
    --faq-surface: #211d27;
    --faq-surface-soft: #292230;
    --faq-success: #78d6a8;
  }

  html.dark-mode .faq-hero {
    background:
      radial-gradient(circle at 88% 8%, rgba(189, 140, 255, 0.16), transparent 31%),
      linear-gradient(135deg, var(--faq-surface) 0%, var(--faq-surface-soft) 100%);
    box-shadow: 0 18px 48px rgba(0, 0, 0, 0.24);
  }

  html.dark-mode .faq-hero::after {
    border-color: rgba(120, 214, 168, 0.08);
  }

  html.dark-mode .faq-eyebrow {
    background: rgba(120, 214, 168, 0.12);
  }

  html.dark-mode .faq-item {
    box-shadow: 0 5px 18px rgba(0, 0, 0, 0.13);
  }

  html.dark-mode .faq-item:hover,
  html.dark-mode .faq-item[open] {
    border-color: rgba(189, 140, 255, 0.48);
  }

  @media (max-width: 767px) {
    .faq-page {
      padding-bottom: 3rem;
    }

    .faq-hero {
      margin-bottom: 1rem;
      border-radius: 18px;
    }

    .faq-item {
      border-radius: 14px;
    }

    .faq-item summary {
      padding: 1.05rem;
    }

    .faq-answer {
      padding: 0 1.05rem 1.2rem;
      font-size: 0.9375rem;
      line-height: 1.7;
    }

    .faq-chevron {
      width: 1.8rem;
      height: 1.8rem;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .faq-item,
    .faq-breadcrumb a,
    .faq-answer a,
    .faq-chevron svg {
      transition: none;
    }

    .faq-answer {
      animation: none;
    }
  }
`;
